/**
 * Importar un extracto (puro). Del libro V4 a lo que necesita el planificador (movimientos de la cuenta con su
 * _Meta, alias, inicio del año) y de la revisión a las órdenes que lo guardan. Mismas reglas que la web antigua:
 * - Se planifica contra los movimientos de ESA cuenta; «es el mismo» (posible duplicado o vinculado a mano) se recuerda
 *   como alias. Un posible duplicado sin decidir ni se importa ni se recuerda: volverá a salir.
 * - El saldo del extracto pasa a ser el saldo del banco de la cuenta si es más reciente que el que había.
 */
import {
  IMPORT_ADAPTERS, planImport, importDefaultsFor, categorize, reconcileWithStatement, reconcileLive, fromBank, round2, suggestKeyword, normalizeText, DIVIDED, nextMonthTarget,
  type AppLine, type AppMovement, type ExistingForImport, type ImportDefaults, type ImportAlias, type ImportItem, type ImportPlan, type ImportRow, type Part, type Rule, type Category,
} from '@pf/domain';
import { newId, tricountConcept, tricountOrigin, ADJUSTMENT_CATEGORY, ADJUSTMENT_SOURCE, type Command, type MetaRow, type TricountOrigin } from '@pf/workbook';
import { balanceAt, type Computed } from './model.js';
import { itemParts, itemsNote, type ImportAmazon, type OrderGroup, type ProposedItem } from './amazon-match.js';

/** Formato de fichero que corresponde a una cuenta por su nombre o su banco (BBVA, ING, Laboral Kutxa, MyInvestor). */
export function adapterFor(account: string, bank = ''): string {
  const a = `${account} ${bank}`.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
  if (a.includes('BBVA')) return 'BBVA';
  if (/\bING\b/.test(a)) return 'ING';
  if (a.includes('LABORAL') || /\bLK\b/.test(a)) return 'LK';
  if (a.includes('MYINVESTOR')) return 'MYINVESTOR';
  return '';
}

const key = (iso: string) => iso.replace(/-/g, '');

export function existingFor(c: Computed, account: string): ExistingForImport[] {
  const meta = new Map(c.wb.meta.map(m => [m.id, m]));
  return c.wb.movements.filter(m => m.account === account && m.id).map(m => {
    const x = meta.get(m.id);
    return { txnId: m.id, dateKey: key(m.date), valueDateKey: key(x?.valueDate || m.date), amount: m.amount, description: m.concept, row: m.row,
      balanceAfter: x?.balanceAfter ?? null, sourceRef: x?.source.startsWith('sync:') ? x.sourceRef : '', origin: x?.source ?? '' };
  });
}

/** Todos los movimientos de la cuenta (con o sin Id) con su origen: lo que suma en el saldo de la app. */
export function appMovementsFor(c: Computed, account: string): AppMovement[] {
  const meta = new Map(c.wb.meta.map(m => [m.id, m.source]));
  return c.wb.movements.filter(m => m.account === account).map(m => ({ id: m.id || `fila-${m.row}`, date: m.date, amount: m.amount, concept: m.concept, origin: meta.get(m.id) ?? '' }));
}

/** Origen legible de un movimiento de la app. */
export function originLabel(origin: string | undefined): string {
  if (!origin) return 'apuntado a mano';
  if (origin === ADJUSTMENT_SOURCE) return 'ajuste de saldo';
  if (origin === 'shortcut') return 'Atajo de iOS';
  if (origin.startsWith('sync:')) return 'sincronizado del banco';
  if (origin.startsWith('file:')) return 'importado de un extracto';
  return 'apuntado en la app';
}

export const aliasesFor = (c: Computed, account: string): ImportAlias[] =>
  c.wb.aliases.filter(a => a.account === account).map(a => ({ txnId: a.id, date: a.date, amount: a.amount, description: a.description }));

/** Inicio del libro: 1 de enero del año con más movimientos (unos pocos del año anterior no lo mueven). */
export function minDateFor(c: Computed): string {
  const count = new Map<string, number>();
  c.wb.movements.forEach(m => { const y = m.date.slice(0, 4); if (y) count.set(y, (count.get(y) ?? 0) + 1); });
  const top = [...count.entries()].sort((a, b) => b[1] - a[1])[0];
  return top ? `${top[0]}-01-01` : '';
}

/** Compartido/Tricount por defecto de una cuenta (preferencia de cada persona; ver adapters/prefs). */
export interface AccountImportDefaults extends ImportDefaults { account: string }

/** Lo guardado, limpio: forma válida, sin vacíos ni repetidos, y sin cuentas que no marcan nada. */
export function normalizeImportDefaults(v: unknown): AccountImportDefaults[] {
  if (!Array.isArray(v)) return [];
  const names = (x: unknown) => (Array.isArray(x) ? [...new Set(x.filter((n): n is string => typeof n === 'string').map(n => n.trim()).filter(n => n && n.length <= 60))] : []);
  const out = new Map<string, AccountImportDefaults>();
  for (const x of v) {
    if (!x || typeof x !== 'object') continue;
    const account = String((x as { account?: unknown }).account ?? '').trim();
    const tricount = names((x as { tricount?: unknown }).tricount);
    const shared = names((x as { shared?: unknown }).shared).filter(n => !tricount.includes(n));   // Tricount ya implica compartida
    if (account && account.length <= 60 && (shared.length || tricount.length)) out.set(account, { account, shared, tricount });
  }
  return [...out.values()].slice(0, 30);
}

export const importDefaultsOf = (list: AccountImportDefaults[], account: string): ImportDefaults | null => list.find(x => x.account === account) ?? null;

/** Cambia una categoría en la lista de compartidas o de Tricount de una cuenta (marcar Tricount la quita de solo compartidas). */
export function setImportDefault(list: AccountImportDefaults[], account: string, category: string, value: 'none' | 'shared' | 'tricount'): AccountImportDefaults[] {
  const cur = list.find(x => x.account === account) ?? { account, shared: [], tricount: [] };
  const shared = cur.shared.filter(n => n !== category), tricount = cur.tricount.filter(n => n !== category);
  if (value === 'shared') shared.push(category);
  if (value === 'tricount') tricount.push(category);
  return normalizeImportDefaults([...list.filter(x => x.account !== account), { account, shared, tricount }]);
}

export function planFor(c: Computed, account: string, adapterId: string, rows: ImportRow[], defaults: ImportDefaults | null = null): ImportPlan {
  return planImport({ accountId: adapterId, rows, existing: existingFor(c, account), aliases: aliasesFor(c, account), minDate: minDateFor(c), defaults,
    categorize: (d, amount) => categorize(c.wb.rules, c.wb.categories, account, d, amount) });
}

/** Comprobación día a día con el extracto (si trae saldos) o solo de movimientos (MyInvestor). */
export function statementCheck(c: Computed, account: string, rows: ImportRow[], plan: ImportPlan) {
  const a = c.wb.accounts.find(x => x.name === account);
  return reconcileWithStatement({ rows, plan, sheetTxns: existingFor(c, account), startBalance: a ? a.opening + a.archived : 0, minDate: minDateFor(c) });
}

/** Lo que decide el usuario para cada movimiento del extracto en la revisión. */
export interface ReviewDecision {
  include: boolean; category: string; shared: boolean; tricount: boolean; note: string;
  /** «Es el mismo» que este movimiento de la app ('' = no). Excluye include. */
  pairWith: string;
  /** Si la pareja tiene otro importe: corregir el de la app al del banco. */
  fixAmount: boolean;
  /** Palabra clave para crear una regla con la categoría elegida ('' = no). */
  rule: string;
  /** División en categorías (vacío = sin dividir). */
  parts: Array<{ category: string; amount: number; note: string }>;
  /** Casillas que tocó la persona en esta fila: ya no las cambia la categoría. */
  manual?: { shared?: boolean; tricount?: boolean };
  /** «Contar en su mes»: aunque su categoría cuente en el mes siguiente a final de mes, este no (p. ej. una paga extra). */
  ownMonth?: boolean;
  /** Cobro o devolución de Amazon con su email: sus artículos, cada uno con su categoría (de ahí sale la división). */
  amazon?: { set: OrderGroup[]; items: ProposedItem[] };
  /** Cobro de Amazon que puede ser más de un envío: se elige y se reparte después desde Inicio. */
  amazonLater?: boolean;
}

/** Hay email de Amazon y todos sus artículos tienen categoría: al guardar se enlaza con él. */
export const amazonReady = (d: Pick<ReviewDecision, 'amazon'>) => !!d.amazon && d.amazon.items.every(x => x.category);

/**
 * La categoría de una fila de Amazon sale de sus artículos: una sola si todos van a la misma, una parte por categoría si
 * no; si falta alguno, sin categoría (se importa así y se reparte después desde Inicio).
 */
export function applyAmazon(d: ReviewDecision): void {
  const parts = d.amazon ? itemParts(d.amazon.items) : [];
  d.category = parts.length === 1 ? parts[0]!.category : '';
  d.parts = parts.length > 1 ? parts : [];
}

/** Lo encontrado en los emails de Amazon, en las filas nuevas que se van a importar. */
export function applyAmazonFound(decisions: Map<number, ReviewDecision>, found: Map<number, ImportAmazon>): void {
  for (const [key, a] of found) {
    const d = decisions.get(key);
    if (!d?.include) continue;
    // Sin categoría, como se aconseja: al repartirlo, la del cobro se propondría para los artículos que no se reconocen
    if ('several' in a) { d.amazonLater = true; d.category = ''; d.parts = []; continue; }
    d.amazon = { set: a.set, items: a.items };
    applyAmazon(d);
  }
}

/** Si este movimiento cuenta en el mes siguiente (su categoría lo dice y llega a final de mes), y no se ha dejado en su mes. */
export function shiftFor(categories: Category[], adapterId: string, it: Pick<ImportItem, 'amount' | 'date' | 'valueDate'>, d: Pick<ReviewDecision, 'category' | 'parts' | 'ownMonth'>) {
  const t = targetFor(categories, adapterId, it, d);
  return t && t.month !== it.date.slice(0, 7) ? t : null;
}

/** El mes al que lo lleva la regla de final de mes de su categoría, aunque sea el de su fecha (null si no aplica). */
function targetFor(categories: Category[], adapterId: string, it: Pick<ImportItem, 'amount' | 'date' | 'valueDate'>, d: Pick<ReviewDecision, 'category' | 'parts' | 'ownMonth'>) {
  // Dividido: manda la categoría de la parte más grande (p. ej. la nómina con un ajuste aparte)
  const category = d.parts.length ? [...d.parts].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))[0]!.category : d.category;
  if (d.ownMonth || !category) return null;
  return nextMonthTarget(categories, category, it.amount, it.date, it.valueDate, adapterId);
}

/**
 * La regla de final de mes ya cumplida por la fecha: la fecha valor es de un mes y la fecha del movimiento del siguiente
 * (la hipoteca: valor 31 oct, fecha 2 nov). Cuenta en el mes de su fecha, no en el de la fecha valor.
 */
export function ruleKeepsDateMonth(categories: Category[], adapterId: string, it: Pick<ImportItem, 'amount' | 'date' | 'valueDate'>, d: Pick<ReviewDecision, 'category' | 'parts' | 'ownMonth'>) {
  const t = targetFor(categories, adapterId, it, d);
  return t && t.month === it.date.slice(0, 7) && !!it.valueDate && it.valueDate.slice(0, 7) !== it.date.slice(0, 7) ? t : null;
}

/**
 * Mes del informe de un movimiento importado ('' = el de su fecha): el que diga la regla de final de mes de su categoría
 * (p. ej. la nómina del 23, o la hipoteca con valor el 31 y fecha el 2: la de su fecha); si no hay regla, el de la fecha
 * valor cuando no coincide con el de la fecha.
 */
export function reportMonth(categories: Category[], adapterId: string, it: Pick<ImportItem, 'amount' | 'date' | 'valueDate'>, d: Pick<ReviewDecision, 'category' | 'parts' | 'ownMonth'>): string {
  const t = targetFor(categories, adapterId, it, d);
  if (t) return t.month === it.date.slice(0, 7) ? '' : t.month;
  return it.valueDate && it.valueDate.slice(0, 7) !== it.date.slice(0, 7) ? it.valueDate.slice(0, 7) : '';
}

/**
 * Al elegir otra categoría en la revisión: compartido y Tricount pasan a ser los de esa categoría en esta cuenta, salvo
 * las casillas que la persona ya tocó a mano en la fila. Tricount solo en gastos y si se puede enviar (libro y quién eres).
 */
export function applyCategoryDefaults(d: ReviewDecision, amount: number, defaults: ImportDefaults | null, canTricount: boolean): void {
  const x = importDefaultsFor(defaults, d.category);
  if (!d.manual?.shared) d.shared = x.shared;
  if (!d.manual?.tricount) d.tricount = canTricount && amount < 0 && x.tricount && d.shared;
  if (d.tricount) d.shared = true;   // a Tricount siempre es compartido
}

/**
 * Revisión inicial: nuevos sí. Posibles duplicados: si es el mismo día e importe y el único candidato, se propone
 * «es el mismo» (a la vista, se puede cambiar); los demás, sin decidir (ni se importan ni se recuerdan hasta que digas).
 */
export function initialDecision(it: ImportItem): ReviewDecision {
  const same = it.status === 'possible-duplicate' && it.onlyCandidate && !it.suggestion && !!it.matchTxnId;
  // Compartido/Tricount por defecto solo con una categoría propuesta de verdad (la de reserva no se enseña)
  const own = !it.categoryIsDefault;
  return { include: it.status === 'new', category: own ? it.category : '', shared: own && it.shared, tricount: own && it.tricount && it.amount < 0,
    note: '', rule: '', parts: [], pairWith: same ? it.matchTxnId : '', fixAmount: false };
}

/** Un posible duplicado aún sin decidir. */
export const undecided = (it: ImportItem, d: ReviewDecision | undefined) => it.status === 'possible-duplicate' && !!d && !d.include && !d.pairWith;

/** Cuándo un movimiento libre de tu app «se parece» a una fila del extracto: ±7 días e importe a ±25 % (o 5 €). */
export const LINK_NEAR = { days: 7, tolerance: 0.25, minEuros: 5 };

/**
 * Movimientos de tu app que podrían ser esta fila del extracto: los libres (que el extracto no tiene), del mismo
 * signo y no vinculados ya a otra fila. `near` son los parecidos (se enseñan en la fila); `all`, todos (elegir a mano).
 */
export function linkOptions(it: { date: string; amount: number }, live: { extra: AppLine[]; later: AppLine[] } | null, taken: Set<string>): { near: AppLine[]; all: AppLine[] } {
  if (!live) return { near: [], all: [] };
  const days = (a: AppLine) => Math.abs(Date.parse(a.date) - Date.parse(it.date)) / 86_400_000;
  const isNear = (a: AppLine) => days(a) <= LINK_NEAR.days && Math.abs(a.amount - it.amount) <= Math.max(LINK_NEAR.minEuros, Math.abs(it.amount) * LINK_NEAR.tolerance);
  // Primero los parecidos; dentro de cada grupo, el importe más cercano y luego la fecha
  const all = [...live.extra, ...live.later].filter(a => Math.sign(a.amount) === Math.sign(it.amount) && !a.id.startsWith('fila-') && !taken.has(a.id))
    .sort((a, b) => Number(isNear(b)) - Number(isNear(a)) || Math.abs(a.amount - it.amount) - Math.abs(b.amount - it.amount) || days(a) - days(b));
  return { near: all.filter(isNear), all };
}

/** Ajuste de saldo que se guardará con la importación (o desde la cuenta). */
export interface Adjustment { amount: number; date: string; reason: string }

/** Cuadre en vivo de la revisión con lo decidido hasta ahora (los movimientos marcados para eliminar ya no cuentan). */
export function liveCheck(c: Computed, p: { account: string; rows: ImportRow[]; plan: ImportPlan; decisions: Map<number, ReviewDecision>; adjustment: Adjustment | null; deletes?: Set<string> }) {
  const a = c.wb.accounts.find(x => x.name === p.account);
  return reconcileLive({ rows: p.rows, items: p.plan.items, decisions: p.decisions, app: appMovementsFor(c, p.account).filter(m => !p.deletes?.has(m.id)),
    startBalance: a ? a.opening + a.archived : 0, minDate: minDateFor(c), bankBalance: p.plan.bankBalance, adjustment: p.adjustment?.amount ?? 0 });
}

/**
 * El objetivo: el saldo que tiene el banco HOY. Tu app hoy (todo lo apuntado hasta hoy) más lo que cambia al guardar
 * (importar, corregir importes, eliminar, ajuste) frente a ese saldo.
 */
export function todayResult(c: Computed, p: { account: string; today: string; target: number | null; live: { imported: number; fixed: number; adjustment: number } | null;
  deletes: Set<string> }) {
  // Tu app hoy: todo lo apuntado, también lo que el banco anota con fecha posterior (su saldo de hoy ya lo descuenta)
  const mine = c.wb.movements.filter(m => m.account === p.account && m.date);
  const appNow = balanceAt(c, p.account, p.today, p.today);
  const deleted = round2(mine.filter(m => p.deletes.has(m.id)).reduce((t, m) => t + m.amount, 0));
  const change = p.live ? round2(p.live.imported + p.live.fixed + p.live.adjustment - deleted) : round2(-deleted);
  const appAfter = round2(appNow + change);
  return { appNow, change, deleted, appAfter, target: p.target, diff: p.target === null ? null : round2(p.target - appAfter) };
}

/** Órdenes de un ajuste de saldo: la categoría (que no cuenta) si falta y el movimiento marcado en _Meta como ajuste. */
export function adjustmentCommands(c: Computed, account: string, adj: Adjustment, today: string, idOf: () => string = () => newId()): Command[] {
  const out: Command[] = [];
  if (!c.wb.categories.some(x => x.name.toLowerCase() === ADJUSTMENT_CATEGORY.toLowerCase()))
    out.push({ type: 'add-category', category: { name: ADJUSTMENT_CATEGORY, kind: 'excluded', nature: '', keywords: [] } });
  const id = idOf();
  const reason = adj.reason.trim().slice(0, 150);
  out.push({ type: 'import-batch', label: `Ajuste de saldo ${account}`, movements: [{ id, date: adj.date, concept: `Ajuste de saldo: ${reason}`, amount: round2(adj.amount), account,
    category: ADJUSTMENT_CATEGORY, shared: false, group: '', note: reason, month: '' }],
    meta: [{ id, source: ADJUSTMENT_SOURCE, sourceRef: '', valueDate: '', balanceAfter: null, tricountSentAt: '', tricountAmount: null, importedAt: today }] });
  return out;
}

export const reviewable = (plan: ImportPlan) => plan.items.filter(i => i.status === 'new' || i.status === 'possible-duplicate');

/** Palabra clave propuesta para una regla (nunca "PAGO CON TARJETA": el comercio). */
export const ruleKeyword = (description: string) => suggestKeyword(description);

export interface CommitInput {
  account: string; adapterId: string; fileName: string; plan: ImportPlan; decisions: Map<number, ReviewDecision>; today: string;
  tricountPayer: string; currentBankDate: string;
  /** Origen en _Meta: 'file:BBVA' (por defecto) o 'sync:enablebanking' (entonces la referencia es la del banco). */
  source?: string;
  /** Para corregir importes de la app: sus movimientos actuales (fila, huella, importe y origen). */
  computed?: Computed;
  adjustment?: Adjustment | null;
  /** Movimientos de la app que se eliminan (no estaban en el banco). */
  deletes?: string[];
  /** Saldo del banco hoy (el objetivo): se guarda como saldo del banco con la fecha de hoy. */
  bankToday?: number | null;
  /** Categorías (con las creadas en la revisión): deciden qué cuenta en el mes siguiente. */
  categories?: Category[];
}

/** Órdenes que guardan la revisión, en orden: movimientos y _Meta, divisiones y enlaces con los emails de Amazon, reglas, alias, saldo del banco y Tricount. */
export function commitCommands(p: CommitInput, idOf: () => string = () => newId()): { commands: Command[]; imported: number; aliases: number } {
  const items = reviewable(p.plan).filter(i => p.decisions.get(i.fileIndex)?.include)
    .sort((a, b) => (a.valueDate + a.date).localeCompare(b.valueDate + b.date) || a.fileIndex - b.fileIndex);
  const rows = items.map(it => {
    const d = p.decisions.get(it.fileIndex)!;
    const id = idOf();
    const parts = d.parts.filter(x => x.category && x.amount);
    const month = reportMonth(p.categories ?? p.computed?.wb.categories ?? [], p.adapterId, it, d);
    const amazon = amazonReady(d) ? d.amazon! : null;
    const note = d.note.trim() || (amazon ? itemsNote(amazon.items, it.amount > 0) : '');
    return { it, d, id, parts, amazon, movement: { id, date: it.date, concept: it.description, amount: it.amount, account: p.account,
      category: parts.length ? DIVIDED : d.category, shared: d.shared || (d.tricount && it.amount < 0 && !!p.tricountPayer), group: '', note, month } };
  });
  const source = p.source ?? `file:${p.adapterId}`;
  const meta: MetaRow[] = rows.map(r => ({ id: r.id, source, sourceRef: source.startsWith('sync:') ? r.it.sourceRef : p.fileName.slice(0, 120),
    valueDate: r.it.valueDate !== r.it.date ? r.it.valueDate : '', balanceAfter: r.it.balanceAfter, tricountSentAt: '', tricountAmount: null, importedAt: p.today }));
  const commands: Command[] = [];
  if (rows.length) commands.push({ type: 'import-batch', label: `${p.account}: ${rows.length}`, movements: rows.map(r => r.movement), meta });
  for (const r of rows) {
    // En la revisión las partes van en positivo; se guardan con el signo del movimiento (como al dividir en el detalle)
    const sign = r.it.amount < 0 ? -1 : 1;
    if (r.parts.length) commands.push({ type: 'save-parts', movementId: r.id, expected: [], parts: r.parts.map(x => ({ movementId: r.id, category: x.category, amount: round2(sign * x.amount), note: x.note }) as Part) });
    if (r.amazon) commands.push({ type: 'link-orders', refs: r.amazon.set.map(g => g.ref), movementId: r.id });
  }
  const rules = new Map<string, Rule>();
  for (const r of rows) {
    const kw = normalizeText(r.d.rule);   // como se comparan los conceptos: mayúsculas y sin acentos
    if (kw.length >= 3 && r.d.category && !r.parts.length) rules.set(`${kw}|${r.d.category}`, { priority: 100, account: '', contains: kw, regex: '', amountEquals: null,
      direction: r.it.amount < 0 ? 'expense' : 'income', category: r.d.category, active: true });
  }
  rules.forEach(rule => commands.push({ type: 'add-rule', rule }));
  // «Es el mismo» (posible duplicado o vinculado a mano): se recuerda para la próxima vez; si tenía otro importe y era
  // algo apuntado a mano, se corrige al del banco
  const paired = reviewable(p.plan).filter(i => p.decisions.get(i.fileIndex)?.pairWith && !p.decisions.get(i.fileIndex)?.include);
  const aliases = paired.map(i => ({ account: p.account, date: i.date, amount: i.amount, description: i.description, id: p.decisions.get(i.fileIndex)!.pairWith }));
  if (aliases.length) commands.push({ type: 'add-aliases', aliases });
  if (p.computed) {
    const meta = new Map(p.computed.wb.meta.map(m => [m.id, m.source]));
    for (const i of paired) {
      const d = p.decisions.get(i.fileIndex)!;
      const m = p.computed.wb.movements.find(x => x.id === d.pairWith);
      if (d.fixAmount && m && Math.abs(m.amount - i.amount) >= 0.005 && !fromBank(meta.get(m.id)))
        commands.push({ type: 'update-movement', id: m.id, row: m.row, fingerprint: m.fingerprint, changes: { amount: i.amount } });
    }
    for (const id of p.deletes ?? []) {
      const m = p.computed.wb.movements.find(x => x.id === id && x.account === p.account);
      if (m) commands.push({ type: 'delete-movement', id: m.id, row: m.row, fingerprint: m.fingerprint });
    }
    if (p.adjustment && Math.abs(p.adjustment.amount) >= 0.005) commands.push(...adjustmentCommands(p.computed, p.account, p.adjustment, p.today));
  }
  const bb = p.plan.bankBalance;
  if (p.bankToday !== undefined && p.bankToday !== null) commands.push({ type: 'set-bank-balance', account: p.account, balance: round2(p.bankToday), date: p.today });
  else if (bb && (!p.currentBankDate || bb.date >= p.currentBankDate)) commands.push({ type: 'set-bank-balance', account: p.account, balance: bb.amount, date: bb.date });
  for (const r of rows) {
    if (!r.d.tricount || r.it.amount >= 0 || !p.tricountPayer) continue;
    commands.push({ type: 'send-tricount', movementId: r.id, sentAt: p.today, expense: { date: r.it.date, concept: tricountConcept(r.d.note, r.it.description),
      amount: round2(Math.abs(r.it.amount)), payer: p.tricountPayer, origin: tricountOrigin(p.account) as TricountOrigin } });
  }
  return { commands, imported: rows.length, aliases: aliases.length };
}

export const adapterLabel = (id: string) => IMPORT_ADAPTERS[id]?.label ?? id;
